import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  SelectChangeEvent,
} from '@mui/material';
import { getFetchErrorMessage } from '../services/apiService';

interface User {
  id: string;
  userName?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  emailConfirmed: boolean;
  phoneNumberConfirmed: boolean;
  twoFactorEnabled: boolean;
  lockoutEnabled: boolean;
  isActive: boolean;
  roles: string[];
}

interface Role {
  id: string;
  name: string;
}

interface UpdateUserData {
  userName: string;
  email?: string;
  phoneNumber?: string;
  emailConfirmed: boolean;
  phoneNumberConfirmed: boolean;
  twoFactorEnabled: boolean;
  lockoutEnabled: boolean;
  isActive: boolean;
  roleIds: string[];
}

interface EditUserDialogProps {
  open: boolean;
  user: User | null;
  onClose: () => void;
  onSuccess: () => void;
}

const EditUserDialog: React.FC<EditUserDialogProps> = ({
  open,
  user,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<UpdateUserData>({
    userName: '',
    email: '',
    phoneNumber: '',
    emailConfirmed: false,
    phoneNumberConfirmed: false,
    twoFactorEnabled: false,
    lockoutEnabled: false,
    isActive: true,
    roleIds: [],
  });

  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load user data when dialog opens
  useEffect(() => {
    if (open && user) {
      setFormData({
        userName: user.userName || '',
        email: user.email || '',
        phoneNumber: user.phoneNumber || '',
        emailConfirmed: user.emailConfirmed,
        phoneNumberConfirmed: user.phoneNumberConfirmed,
        twoFactorEnabled: user.twoFactorEnabled,
        lockoutEnabled: user.lockoutEnabled,
        isActive: user.isActive ?? true,
        roleIds: [],
      });
      loadRoles();
      setError(null);
    }
  }, [open, user]);

  // Convert role names to role IDs when roles are loaded
  useEffect(() => {
    if (roles.length > 0 && user) {
      const userRoleIds = roles
        .filter(role => user.roles.includes(role.name))
        .map(role => role.id);
      
      setFormData(prev => ({
        ...prev,
        roleIds: userRoleIds
      }));
    }
  }, [roles, user]);

  const loadRoles = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${apiUrl}/roles`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setRoles(data);
      } else {
        throw new Error(await getFetchErrorMessage(response, 'Hiba történt a szerepek betöltése során.'));
      }
    } catch (error) {
      console.error('Error loading roles:', error);
      setError('Hiba történt a szerepek betöltése során');
    }
  };

  const handleInputChange = (field: keyof UpdateUserData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleRoleChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    handleInputChange('roleIds', typeof value === 'string' ? value.split(',') : value);
  };

  const handleSubmit = async () => {
    if (!user) return;

    // Validation
    if (!formData.userName.trim()) {
      setError('A felhasználónév megadása kötelező!');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const payload: Record<string, unknown> = {
        userName: formData.userName.trim(),
        phoneNumber: formData.phoneNumber ?? '',
        emailConfirmed: formData.emailConfirmed,
        phoneNumberConfirmed: formData.phoneNumberConfirmed,
        twoFactorEnabled: formData.twoFactorEnabled,
        lockoutEnabled: formData.lockoutEnabled,
        isActive: formData.isActive,
        roleIds: formData.roleIds,
      };

      if (formData.email?.trim()) {
        payload.email = formData.email.trim();
      }

      const response = await fetch(`${apiUrl}/users/${user.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        onSuccess();
        onClose();
      } else {
        throw new Error(await getFetchErrorMessage(response, 'Hiba történt a felhasználó frissítése során.'));
      }
    } catch (error: any) {
      console.error('Error updating user:', error);
      setError(error.message || 'Hiba történt a felhasználó frissítése során');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  const getSelectedRoleNames = () => {
    return roles
      .filter(role => formData.roleIds.includes(role.id))
      .map(role => role.name);
  };

  if (!user) return null;

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { minHeight: '600px' }
      }}
    >
      <DialogTitle>Felhasználó szerkesztése: {user.userName}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Basic Information */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <TextField
              label="Felhasználónév"
              value={formData.userName}
              onChange={(e) => handleInputChange('userName', e.target.value)}
              required
              fullWidth
            />
            <TextField
              label="Email cím"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              fullWidth
            />
            <TextField
              label="Telefonszám"
              value={formData.phoneNumber}
              onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
              fullWidth
            />
          </Box>

          {/* Roles Selection */}
          <FormControl fullWidth>
            <InputLabel>Szerepkörök</InputLabel>
            <Select
              multiple
              value={formData.roleIds}
              onChange={handleRoleChange}
              input={<OutlinedInput label="Szerepkörök" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {getSelectedRoleNames().map((name) => (
                    <Chip key={name} label={name} size="small" />
                  ))}
                </Box>
              )}
            >
              {roles.map((role) => (
                <MenuItem key={role.id} value={role.id}>
                  {role.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Confirmation Switches */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.emailConfirmed}
                  onChange={(e) => handleInputChange('emailConfirmed', e.target.checked)}
                />
              }
              label="Email megerősítve"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.phoneNumberConfirmed}
                  onChange={(e) => handleInputChange('phoneNumberConfirmed', e.target.checked)}
                />
              }
              label="Telefonszám megerősítve"
            />
          </Box>

          {/* Security Switches */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => handleInputChange('isActive', e.target.checked)}
                  color="success"
                />
              }
              label={formData.isActive ? 'Aktív felhasználó' : 'Inaktív felhasználó'}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.twoFactorEnabled}
                  onChange={(e) => handleInputChange('twoFactorEnabled', e.target.checked)}
                />
              }
              label="Kétfaktoros hitelesítés"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.lockoutEnabled}
                  onChange={(e) => handleInputChange('lockoutEnabled', e.target.checked)}
                />
              }
              label="Kizárás engedélyezve"
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button 
          onClick={handleClose} 
          disabled={loading}
        >
          Mégse
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Mentés...' : 'Mentés'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditUserDialog;
